import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { addChatMembership } from './chatService';

/**
 * Group Chat Invitation Service
 *
 * Handles group chat invitations where users must accept before joining
 */

/**
 * Send group chat invitations to multiple users
 *
 * @param {string} groupChatId - ID of the group chat
 * @param {string} groupName - Name of the group
 * @param {string} inviterId - ID of user sending invitations
 * @param {string} inviterName - Display name of inviter
 * @param {string[]} userIds - Array of user IDs to invite
 * @returns {Promise<void>}
 */
export async function sendGroupInvitations(groupChatId, groupName, inviterId, inviterName, userIds) {
  try {
    // Create invitation document for each user
    const invitationPromises = userIds.map(async (userId) => {
      // Check if invitation already exists
      const existingQuery = query(
        collection(db, 'groupChatInvitations'),
        where('groupChatId', '==', groupChatId),
        where('invitedUserId', '==', userId),
        where('status', '==', 'pending')
      );

      const existingDocs = await getDocs(existingQuery);

      // If invitation already exists, don't create duplicate
      if (!existingDocs.empty) {
        console.log(`Invitation already exists for user ${userId} in group ${groupChatId}`);
        return;
      }

      // Create new invitation
      const invitationData = {
        groupChatId,
        groupName,
        invitedUserId: userId,
        invitedBy: inviterId,
        invitedByName: inviterName,
        status: 'pending',
        createdAt: serverTimestamp(),
        respondedAt: null,
        expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)) // 7 days from now
      };

      return addDoc(collection(db, 'groupChatInvitations'), invitationData);
    });

    await Promise.all(invitationPromises);
    console.log(`Sent ${userIds.length} group chat invitations for group ${groupChatId}`);
  } catch (error) {
    console.error('Error sending group invitations:', error);
    throw error;
  }
}

/**
 * Accept a group chat invitation
 *
 * @param {string} invitationId - ID of the invitation
 * @param {string} userId - ID of user accepting
 * @returns {Promise<void>}
 */
export async function acceptGroupInvitation(invitationId, userId) {
  try {
    const invitationRef = doc(db, 'groupChatInvitations', invitationId);

    // Update invitation status
    await updateDoc(invitationRef, {
      status: 'accepted',
      respondedAt: serverTimestamp()
    });

    // Get invitation data to add user to group chat
    const invitationDoc = await getDocs(
      query(
        collection(db, 'groupChatInvitations'),
        where('__name__', '==', invitationId)
      )
    );

    if (invitationDoc.empty) {
      throw new Error('Invitation not found');
    }

    const invitation = invitationDoc.docs[0].data();

    // Add user to group chat membership
    await addChatMembership(userId, invitation.groupChatId, 'group');

    console.log(`User ${userId} accepted invitation to group ${invitation.groupChatId}`);
  } catch (error) {
    console.error('Error accepting group invitation:', error);
    throw error;
  }
}

/**
 * Decline a group chat invitation
 *
 * @param {string} invitationId - ID of the invitation
 * @param {string} userId - ID of user declining
 * @returns {Promise<void>}
 */
export async function declineGroupInvitation(invitationId, userId) {
  try {
    const invitationRef = doc(db, 'groupChatInvitations', invitationId);

    // Update invitation status
    await updateDoc(invitationRef, {
      status: 'declined',
      respondedAt: serverTimestamp()
    });

    console.log(`User ${userId} declined invitation ${invitationId}`);
  } catch (error) {
    console.error('Error declining group invitation:', error);
    throw error;
  }
}

/**
 * Get all pending group chat invitations for a user
 *
 * @param {string} userId - ID of user
 * @returns {Promise<Array>} Array of pending invitations
 */
export async function getUserGroupInvitations(userId) {
  try {
    const invitationsQuery = query(
      collection(db, 'groupChatInvitations'),
      where('invitedUserId', '==', userId),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(invitationsQuery);

    const invitations = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter out expired invitations
    const now = Date.now();
    const validInvitations = invitations.filter(inv => {
      if (inv.expiresAt) {
        return inv.expiresAt.toMillis() > now;
      }
      return true; // If no expiry, keep it
    });

    return validInvitations;
  } catch (error) {
    console.error('Error getting user group invitations:', error);
    return [];
  }
}

/**
 * Get all invitations for a specific group chat
 *
 * @param {string} groupChatId - ID of group chat
 * @returns {Promise<Array>} Array of invitations for the group
 */
export async function getGroupInvitations(groupChatId) {
  try {
    const invitationsQuery = query(
      collection(db, 'groupChatInvitations'),
      where('groupChatId', '==', groupChatId)
    );

    const snapshot = await getDocs(invitationsQuery);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error getting group invitations:', error);
    return [];
  }
}
